import { useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueries } from '@tanstack/react-query'
import {
  Users, CheckCircle2, Calendar, TrendingUp,
  Loader2, Plus, ArrowUpRight,
  LayoutDashboard,
  Leaf,
  ArrowDown, ArrowUp,
  Search, X,
  BookOpen, Palmtree, PartyPopper,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PageTitle } from '@/shared/components'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { etudiantsApi } from '@/features/etudiants/api/etudiants.api'
import { agendaApi } from '@/features/etudiants/api/agenda.api'
import { periodesApi } from '@/features/periodes/api/periodes.api'
import { eventsApi } from '@/features/events/api/events.api'
import type { Etudiant } from '@/shared/types/etudiant'
import type { AgendaCompletDto } from '@/shared/types/agenda'
import type { EvenementEtudiantDTO } from '@/shared/types/evenement'

// ── Helpers ───────────────────────────────────────────────

function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getInitials(prenom: string, nom: string) {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

type StatusKey = 'excellent' | 'encours' | 'surveiller' | 'none'

function statusInfo(pct: number, hasTasks: boolean): { text: string; bg: string; bar: string; key: StatusKey } {
  if (!hasTasks)
    return { text: 'text-neutral-400', bg: 'bg-neutral-100', bar: '#d4d4d4', key: 'none' }
  if (pct >= 80)
    return { text: 'text-emerald-600', bg: 'bg-emerald-50', bar: '#22c55e', key: 'excellent' }
  if (pct >= 30)
    return { text: 'text-amber-600', bg: 'bg-amber-50', bar: '#f59e0b', key: 'encours' }
  return { text: 'text-neutral-500', bg: 'bg-neutral-100', bar: '#a3a3a3', key: 'surveiller' }
}

function heatmapLevel(pct: number): 0 | 1 | 2 | 3 | 4 {
  if (pct >= 80) return 4
  if (pct >= 50) return 3
  if (pct >= 20) return 2
  if (pct > 0) return 1
  return 0
}

const HEATMAP_COLORS = ['bg-neutral-100', 'bg-neutral-300', 'bg-amber-200', 'bg-emerald-200', 'bg-emerald-500']
const HEATMAP_TEXT = ['text-neutral-400', 'text-neutral-700', 'text-amber-700', 'text-emerald-700', 'text-white']

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

function getWeekDates(today: Date): Date[] {
  const day = today.getDay()
  const monday = new Date(today)
  monday.setDate(today.getDate() - ((day === 0 ? 7 : day) - 1))
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return d
  })
}

function formatEventDate(dateStr: string, today: string, tomorrow: string, t: (k: string) => string): string {
  if (dateStr === today) return t('dashboard.formateur.alerts.today')
  if (dateStr === tomorrow) return t('dashboard.formateur.alerts.tomorrow')
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
}

// ── Types ─────────────────────────────────────────────────

interface StudentRow {
  etudiant: Etudiant
  agenda: AgendaCompletDto | null
  totalTaches: number
  tachesTerminees: number
  progressPct: number
  isLoading: boolean
  scanDone: boolean
  scanExists: boolean
  tachesReportees: number
  tachesLangueTotal: number
  tachesLangueTerminees: number
  tempsRestantMin: number
  events: EvenementEtudiantDTO[]
}

interface Alert {
  etudiant: Etudiant
  type: 'retard' | 'reports'
  title: string
  description: string
}

type AlertFilter = 'all' | 'retard' | 'reports'
type HeatmapFilter = 'all' | 0 | 1 | 2 | 3 | 4
type ScanFilter = 'all' | 'done' | 'notdone'
type EventFilter = 'all' | 'CONTROLE' | 'VACANCES' | 'RELAX'

// ── Data hook ─────────────────────────────────────────────

function useDashboardData() {
  const today = useMemo(() => new Date(), [])
  const todayStr = fmtDate(today)
  const tomorrowDate = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() + 1); return d }, [today])
  const tomorrowStr = fmtDate(tomorrowDate)
  const weekDates = useMemo(() => getWeekDates(today), [today])
  const todayDayIndex = useMemo(() => {
    const d = today.getDay()
    return d === 0 ? 6 : d - 1
  }, [today])

  const { data: etudiants = [], isLoading: loadingEtudiants } = useQuery({
    queryKey: ['etudiants'],
    queryFn: etudiantsApi.getAll,
  })

  const { data: periodes } = useQuery({
    queryKey: ['periodes', 'actifs'],
    queryFn: periodesApi.getActifs,
  })

  const agendaQueries = useQueries({
    queries: etudiants.map((et) => ({
      queryKey: ['agenda', et.id, todayStr],
      queryFn: () => agendaApi.getAgendaComplet(et.id, today),
      enabled: etudiants.length > 0,
      staleTime: 2 * 60 * 1000,
      retry: 1,
    })),
  })

  const weekAgendaQueries = useQueries({
    queries: etudiants.flatMap((et) =>
      weekDates.slice(0, todayDayIndex + 1).map((d) => ({
        queryKey: ['agenda', et.id, fmtDate(d)],
        queryFn: () => agendaApi.getAgendaComplet(et.id, d),
        enabled: etudiants.length > 0,
        staleTime: 5 * 60 * 1000,
        retry: 1,
      })),
    ),
  })

  const eventsQueries = useQueries({
    queries: etudiants.map((et) => ({
      queryKey: ['evenements', 'etudiant', et.id],
      queryFn: () => eventsApi.getByEtudiantId(et.id),
      enabled: etudiants.length > 0,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  })

  const periodeEnCours = useMemo(() => {
    if (!periodes) return null
    for (const p of periodes) {
      const dp = p.datesPeriodes?.find((dp) => todayStr >= dp.dateDebut && todayStr <= dp.dateFin)
      if (dp) return { ...p, libelle: dp.libelle }
    }
    return null
  }, [periodes, todayStr])

  const rows: StudentRow[] = useMemo(() =>
    etudiants.map((etudiant, i) => {
      const q = agendaQueries[i]
      const agenda = q?.data ?? null
      const isLoading = q?.isLoading ?? true
      const events = eventsQueries[i]?.data ?? []

      if (!agenda) {
        return {
          etudiant, agenda: null, totalTaches: 0, tachesTerminees: 0, progressPct: 0,
          isLoading, scanDone: false, scanExists: false, tachesReportees: 0,
          tachesLangueTotal: 0, tachesLangueTerminees: 0, tempsRestantMin: 0, events,
        }
      }

      const total = agenda.taches.length + agenda.tachesLangue.length + (agenda.tacheScan ? 1 : 0)
      const done =
        agenda.taches.filter((t) => t.termine).length +
        agenda.tachesLangue.filter((t) => t.termine).length +
        (agenda.tacheScan?.termine ? 1 : 0)

      const tempsRestant = agenda.taches
        .filter((t) => !t.termine)
        .reduce((sum, t) => sum + t.action.dureeMinutes, 0)

      return {
        etudiant,
        agenda,
        totalTaches: total,
        tachesTerminees: done,
        progressPct: total > 0 ? Math.round((done / total) * 100) : 0,
        isLoading,
        scanDone: agenda.tacheScan?.termine ?? false,
        scanExists: agenda.tacheScan !== null,
        tachesReportees: agenda.tachesReportees?.length ?? 0,
        tachesLangueTotal: agenda.tachesLangue.length,
        tachesLangueTerminees: agenda.tachesLangue.filter((t) => t.termine).length,
        tempsRestantMin: tempsRestant,
        events,
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [etudiants, agendaQueries.map((q) => q.dataUpdatedAt).join(','), eventsQueries.map((q) => q.dataUpdatedAt).join(',')],
  )

  const daysCount = todayDayIndex + 1
  const weekHeatmap = useMemo(() => {
    return etudiants.map((et, etIdx) => {
      const dayPcts: (number | null)[] = Array.from({ length: 7 }, (_, dayIdx) => {
        if (dayIdx > todayDayIndex) return null
        const qIndex = etIdx * daysCount + dayIdx
        const agenda = weekAgendaQueries[qIndex]?.data
        if (!agenda) return null
        const total = agenda.taches.length + agenda.tachesLangue.length + (agenda.tacheScan ? 1 : 0)
        if (total === 0) return null
        const done =
          agenda.taches.filter((t) => t.termine).length +
          agenda.tachesLangue.filter((t) => t.termine).length +
          (agenda.tacheScan?.termine ? 1 : 0)
        return Math.round((done / total) * 100)
      })
      return { etudiant: et, dayPcts }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiants, todayDayIndex, daysCount, weekAgendaQueries.map((q) => q.dataUpdatedAt).join(',')])

  const alerts: Alert[] = useMemo(() => {
    const result: Alert[] = []
    for (const row of rows) {
      if (row.isLoading) continue
      if (row.totalTaches > 0 && row.progressPct === 0) {
        const parts = []
        if (row.scanExists && !row.scanDone) parts.push('Scan non fait')
        parts.push(`0/${row.totalTaches}`)
        result.push({
          etudiant: row.etudiant,
          type: 'retard',
          title: `${row.etudiant.prenom} ${row.etudiant.nom} — 0%`,
          description: parts.join(' · '),
        })
      }
      if (row.tachesReportees >= 2) {
        result.push({
          etudiant: row.etudiant,
          type: 'reports',
          title: `${row.etudiant.prenom} ${row.etudiant.nom} — ${row.tachesReportees} reports`,
          description: 'Report accumulé',
        })
      }
    }
    return result.sort((a, b) => {
      const order = { retard: 0, reports: 1 }
      return order[a.type] - order[b.type]
    })
  }, [rows, todayStr])

  const allEvents = useMemo(() => {
    const map = new Map<number, { event: EvenementEtudiantDTO; studentIds: number[]; studentNames: string[]; studentFullNames: string[] }>()
    for (const row of rows) {
      for (const evt of row.events) {
        if (evt.dateDebut < todayStr) continue
        const existing = map.get(evt.id)
        if (existing) {
          existing.studentIds.push(row.etudiant.id)
          existing.studentNames.push(row.etudiant.prenom)
          existing.studentFullNames.push(`${row.etudiant.prenom} ${row.etudiant.nom}`)
        } else {
          map.set(evt.id, { event: evt, studentIds: [row.etudiant.id], studentNames: [row.etudiant.prenom], studentFullNames: [`${row.etudiant.prenom} ${row.etudiant.nom}`] })
        }
      }
    }
    return [...map.values()]
      .sort((a, b) => a.event.dateDebut.localeCompare(b.event.dateDebut))
      .slice(0, 10)
  }, [rows, todayStr])

  const totalTaches = rows.reduce((s, e) => s + e.totalTaches, 0)
  const totalTerminees = rows.reduce((s, e) => s + e.tachesTerminees, 0)
  const tauxCompletion = totalTaches > 0 ? Math.round((totalTerminees / totalTaches) * 100) : 0
  const scansDone = rows.filter((r) => r.scanDone).length
  const scansTotal = rows.filter((r) => r.scanExists).length

  return {
    rows, alerts, allEvents, weekHeatmap,
    totalEtudiants: etudiants.length, totalTaches, totalTerminees, tauxCompletion,
    scansDone, scansTotal,
    periodeEnCours, todayDayIndex,
    todayStr, tomorrowStr,
    loading: loadingEtudiants,
    loadingAgendas: agendaQueries.some((q) => q.isLoading),
  }
}

// ── Sub-components ────────────────────────────────────────

function Avatar({ etudiant, size = 'md' }: { etudiant: Etudiant; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'md' ? 'h-8 w-8' : 'h-6 w-6'
  const textSize = size === 'md' ? 'text-[11px]' : 'text-[9px]'

  if (etudiant.photoUrl) {
    return <img src={etudiant.photoUrl} alt="" className={cn(sizeClass, 'rounded-full object-cover')} />
  }
  return (
    <div className={cn(sizeClass, textSize, 'flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 font-bold text-white')}>
      {getInitials(etudiant.prenom, etudiant.nom)}
    </div>
  )
}

function KpiCard({
  icon: Icon, iconBg, iconColor, label, value, sub, barPct, barColor, change,
}: {
  icon: typeof Users; iconBg: string; iconColor: string
  label: string; value: string | number; sub?: string
  barPct?: number; barColor?: string; change?: number | null
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', iconBg)}>
          <Icon className={cn('h-5 w-5', iconColor)} />
        </div>
        {change != null && change !== 0 && (
          <span className={cn(
            'flex items-center gap-0.5 rounded-lg px-2 py-0.5 text-xs font-bold',
            change > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500',
          )}>
            {change > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {change > 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <div className="mt-3 text-2xl font-extrabold text-neutral-900">
        {value}
        {sub && <span className="text-base font-normal text-neutral-400">{sub}</span>}
      </div>
      <p className="mt-1 text-xs text-neutral-400">{label}</p>
      {barPct != null && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${barPct}%`, background: barColor }}
          />
        </div>
      )}
    </div>
  )
}

function FilterPill({ label, count, active, onClick }: {
  label: string; count?: number; active: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex h-7 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-[11px] font-semibold transition-all',
        active
          ? 'border-primary bg-primary text-white'
          : 'border-neutral-200 bg-white text-neutral-500 hover:border-primary hover:text-primary',
      )}
    >
      {label}
      {count != null && (
        <span className={cn(
          'rounded-full px-1.5 text-[9px] font-bold leading-4',
          active ? 'bg-white/25 text-white' : 'bg-primary/10 text-primary',
        )}>
          {count}
        </span>
      )}
    </button>
  )
}

// ── Main page ─────────────────────────────────────────────

export function DashboardDesktopPage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [alertFilter, setAlertFilter] = useState<AlertFilter>('all')
  const [heatmapFilter, setHeatmapFilter] = useState<HeatmapFilter>('all')
  const [scanFilter, setScanFilter] = useState<ScanFilter>('all')
  const [eventFilter, setEventFilter] = useState<EventFilter>('all')

  // Global student search
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const {
    rows, alerts, allEvents, weekHeatmap,
    totalEtudiants, totalTaches, totalTerminees, tauxCompletion,
    scansDone, scansTotal,
    periodeEnCours, todayDayIndex,
    todayStr, tomorrowStr,
    loading, loadingAgendas,
  } = useDashboardData()

  // Search suggestions
  const suggestions = useMemo(() => {
    if (!searchQuery.trim()) return []
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const full = `${r.etudiant.prenom} ${r.etudiant.nom}`.toLowerCase()
      return full.includes(q)
    }).slice(0, 8)
  }, [rows, searchQuery])

  const selectedStudent = useMemo(
    () => selectedStudentId ? rows.find((r) => r.etudiant.id === selectedStudentId) ?? null : null,
    [rows, selectedStudentId],
  )

  const handleSelectStudent = (id: number) => {
    setSelectedStudentId(id)
    setSearchQuery('')
    setShowSuggestions(false)
  }

  const handleClearStudent = () => {
    setSelectedStudentId(null)
    setSearchQuery('')
  }

  // Students: sorted by progress asc, filtered by selected student
  const studentList = useMemo(() => {
    const source = selectedStudentId ? rows.filter((r) => r.etudiant.id === selectedStudentId) : rows
    return [...source].sort((a, b) => a.progressPct - b.progressPct)
  }, [rows, selectedStudentId])

  // Alerts: filtered by type + selected student
  const filteredAlerts = useMemo(() => {
    let result = alerts
    if (selectedStudentId) result = result.filter((a) => a.etudiant.id === selectedStudentId)
    if (alertFilter !== 'all') result = result.filter((a) => a.type === alertFilter)
    return result
  }, [alerts, alertFilter, selectedStudentId])

  const alertCounts = useMemo(() => {
    const c = { all: alerts.length, retard: 0, reports: 0 }
    alerts.forEach((a) => c[a.type]++)
    return c
  }, [alerts])

  // Heatmap: filtered by color level + selected student
  const filteredHeatmap = useMemo(() => {
    let result = weekHeatmap
    if (selectedStudentId) result = result.filter((row) => row.etudiant.id === selectedStudentId)
    if (heatmapFilter !== 'all') {
      result = result.filter((row) => {
        const r = rows.find((r) => r.etudiant.id === row.etudiant.id)
        if (!r) return false
        return heatmapLevel(r.progressPct) === heatmapFilter
      })
    }
    return result
  }, [weekHeatmap, heatmapFilter, rows, selectedStudentId])

  const heatmapCounts = useMemo(() => {
    const c = { all: rows.length, 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }
    rows.forEach((r) => {
      const lvl = heatmapLevel(r.progressPct)
      c[lvl]++
    })
    return c
  }, [rows])

  // Scans: filtered by scan status + selected student
  const scansRows = useMemo(() => {
    let result = selectedStudentId ? rows.filter((r) => r.etudiant.id === selectedStudentId) : rows
    if (scanFilter === 'done') result = result.filter((r) => r.scanDone)
    if (scanFilter === 'notdone') result = result.filter((r) => !r.scanDone)
    return result
  }, [rows, scanFilter, selectedStudentId])

  const scanCounts = useMemo(() => ({
    all: rows.length,
    done: rows.filter((r) => r.scanDone).length,
    notdone: rows.filter((r) => !r.scanDone).length,
  }), [rows])

  // Events: filtered by type + selected student
  const filteredEvents = useMemo(() => {
    let result = allEvents
    if (selectedStudentId) result = result.filter((e) => e.studentIds.includes(selectedStudentId))
    if (eventFilter !== 'all') result = result.filter((e) => e.event.type === eventFilter)
    return result
  }, [allEvents, eventFilter, selectedStudentId])

  const eventCounts = useMemo(() => {
    const c = { all: allEvents.length, CONTROLE: 0, VACANCES: 0, RELAX: 0 }
    allEvents.forEach((e) => c[e.event.type]++)
    return c
  }, [allEvents])

  if (!user || user.role === 'etudiant') return null

  const ALERT_BADGE = {
    retard: { bg: 'bg-neutral-100', text: 'text-neutral-600' },
    reports: { bg: 'bg-amber-50', text: 'text-amber-600' },
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <PageTitle
        icon={LayoutDashboard}
        title={t('dashboard.title')}
        subtitle={(() => {
          const d = new Date()
          const fmt = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
          const capitalized = fmt.replace(/\b\w/g, (c) => c.toUpperCase())
          const periode = periodeEnCours ? ` · ${periodeEnCours.libelle}` : ''
          return `${user.prenom} ${user.nom} — ${capitalized}${periode}`
        })()}
      />

      {/* ── KPI Row ── */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          icon={Users} iconBg="bg-primary/10" iconColor="text-primary"
          label={t('dashboard.formateur.kpi.activeStudents')} value={totalEtudiants}
        />
        <KpiCard
          icon={CheckCircle2} iconBg="bg-emerald-50" iconColor="text-emerald-500"
          label={t('dashboard.formateur.kpi.tasksCompleted')} value={totalTerminees} sub={`/${totalTaches}`}
          barPct={totalTaches > 0 ? Math.round((totalTerminees / totalTaches) * 100) : 0}
          barColor="linear-gradient(90deg,#10b981,#34d399)"
        />
        <KpiCard
          icon={Leaf} iconBg="bg-violet-50" iconColor="text-violet-500"
          label={t('dashboard.formateur.kpi.scansCompleted')} value={scansDone} sub={`/${scansTotal}`}
          barPct={scansTotal > 0 ? Math.round((scansDone / scansTotal) * 100) : 0}
          barColor="linear-gradient(90deg,#8b5cf6,#a78bfa)"
        />
        <KpiCard
          icon={TrendingUp} iconBg="bg-amber-50" iconColor="text-amber-500"
          label={t('dashboard.formateur.kpi.avgCompletion')} value={`${tauxCompletion}`} sub="%"
          barPct={tauxCompletion}
          barColor="linear-gradient(90deg,#f59e0b,#fbbf24)"
        />
      </div>

      {/* ── Student search ── */}
      <div ref={searchRef} className="relative">
        {selectedStudent ? (
          <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5">
            <Search className="h-4 w-4 text-primary" />
            <Avatar etudiant={selectedStudent.etudiant} size="sm" />
            <span className="text-sm font-semibold text-neutral-900">
              {selectedStudent.etudiant.prenom} {selectedStudent.etudiant.nom}
            </span>
            <span className={cn('text-xs font-bold', statusInfo(selectedStudent.progressPct, selectedStudent.totalTaches > 0).text)}>
              {selectedStudent.progressPct}%
            </span>
            <span className="text-xs text-neutral-400">
              {selectedStudent.tachesTerminees}/{selectedStudent.totalTaches} {t('dashboard.formateur.students.tasks')}
            </span>
            <button
              onClick={handleClearStudent}
              className="ml-auto flex h-6 w-6 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true) }}
                onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={t('dashboard.formateur.search.placeholder', 'Rechercher un étudiant...')}
                className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-10 pr-4 text-sm text-neutral-900 placeholder:text-neutral-400 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-xl border border-neutral-200 bg-white py-1 shadow-lg">
                {suggestions.map((row) => {
                  const st = statusInfo(row.progressPct, row.totalTaches > 0)
                  return (
                    <button
                      key={row.etudiant.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectStudent(row.etudiant.id)}
                      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-neutral-50"
                    >
                      <Avatar etudiant={row.etudiant} size="sm" />
                      <span className="flex-1 text-sm font-semibold text-neutral-900">
                        {row.etudiant.prenom} {row.etudiant.nom}
                      </span>
                      <span className={cn('text-xs font-bold', st.text)}>{row.progressPct}%</span>
                      <span className="text-[11px] text-neutral-400">{row.tachesTerminees}/{row.totalTaches}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Two columns ── */}
      <div className="grid grid-cols-[1fr_340px] gap-6">

        {/* ══ LEFT COLUMN ══ */}
        <div className="space-y-6">

          {/* Student list — search only, compact rows, fixed scroll */}
          <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-sm font-bold text-neutral-900">{t('dashboard.formateur.students.title')}</h2>
              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white">
                {studentList.length}
              </span>
              {loadingAgendas && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
            </div>

            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded-lg bg-neutral-100" />
                ))}
              </div>
            ) : studentList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                  <Users className="h-7 w-7 text-neutral-400" />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-neutral-900">{t('dashboard.formateur.students.noStudents')}</h3>
                <p className="mt-1 text-xs text-neutral-500">{t('dashboard.formateur.students.noStudentsDesc')}</p>
                <button
                  onClick={() => navigate('/etudiants/create')}
                  className="mt-3 flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-primary/90"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('dashboard.formateur.students.createStudent')}
                </button>
              </div>
            ) : (
              <div className="max-h-[240px] space-y-0.5 overflow-y-auto">
                {studentList.map((row) => {
                  const st = statusInfo(row.progressPct, row.totalTaches > 0)
                  const niveau = row.etudiant.parcours?.[0]?.niveau

                  return (
                    <div
                      key={row.etudiant.id}
                      onClick={() => navigate(`/etudiants/${row.etudiant.id}`)}
                      className="group flex cursor-pointer items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 transition-all hover:border-primary/20 hover:bg-neutral-50"
                    >
                      <Avatar etudiant={row.etudiant} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-1.5">
                          <span className="truncate text-[13px] font-semibold text-neutral-900 group-hover:text-primary">
                            {row.etudiant.prenom} {row.etudiant.nom}
                          </span>
                          {niveau && <span className="shrink-0 text-[10px] text-neutral-400">{niveau}</span>}
                        </div>
                        {row.isLoading ? (
                          <div className="mt-1 h-1 w-full animate-pulse rounded-full bg-neutral-100" />
                        ) : (
                          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-neutral-100">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${row.progressPct}%`, backgroundColor: st.bar }}
                            />
                          </div>
                        )}
                      </div>
                      {!row.isLoading && (
                        <span className={cn('shrink-0 text-xs font-bold', st.text)}>{row.progressPct}%</span>
                      )}
                      {!row.isLoading && (
                        <span className="shrink-0 text-[11px] text-neutral-400">
                          <span className="font-semibold text-neutral-600">{row.tachesTerminees}</span>/{row.totalTaches}
                        </span>
                      )}
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-neutral-300 transition-colors group-hover:text-primary" />
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Alerts — filters + fixed scroll */}
          {alerts.length > 0 && (
            <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-bold text-neutral-900">
                    {t('dashboard.formateur.alerts.title')}
                  </h2>
                  <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white">
                    {filteredAlerts.length}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FilterPill label={t('dashboard.formateur.alerts.all')} count={alertCounts.all} active={alertFilter === 'all'} onClick={() => setAlertFilter('all')} />
                  <FilterPill label={t('dashboard.formateur.alerts.retard')} count={alertCounts.retard} active={alertFilter === 'retard'} onClick={() => setAlertFilter('retard')} />
                  <FilterPill label={t('dashboard.formateur.alerts.reports')} count={alertCounts.reports} active={alertFilter === 'reports'} onClick={() => setAlertFilter('reports')} />
                </div>
              </div>
              <div className="max-h-[220px] space-y-0.5 overflow-y-auto">
                {filteredAlerts.length === 0 ? (
                  <p className="py-4 text-center text-xs text-neutral-400">{t('dashboard.formateur.alerts.noAlerts')}</p>
                ) : filteredAlerts.map((alert, i) => {
                  const badge = ALERT_BADGE[alert.type]
                  return (
                    <div
                      key={i}
                      onClick={() => navigate(`/etudiants/${alert.etudiant.id}`)}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-[7px] transition-colors hover:bg-neutral-50"
                    >
                      <Avatar etudiant={alert.etudiant} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-neutral-900">{alert.title}</p>
                        <p className="truncate text-[11px] text-neutral-400">{alert.description}</p>
                      </div>
                      <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide', badge.bg, badge.text)}>
                        {t(`dashboard.formateur.alerts.${alert.type}`)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* ══ RIGHT COLUMN ══ */}
        <div className="space-y-5">

          {/* Heatmap — filters + fixed scroll */}
          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-neutral-900">
              <Calendar className="h-4 w-4 text-primary" />
              {t('dashboard.formateur.heatmap.title')}
            </h3>
            <div className="mb-2 flex items-center gap-1.5">
              <FilterPill label={t('dashboard.formateur.heatmap.all')} count={heatmapCounts.all} active={heatmapFilter === 'all'} onClick={() => setHeatmapFilter('all')} />
              {([0, 1, 2, 3, 4] as const).map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setHeatmapFilter(heatmapFilter === lvl ? 'all' : lvl)}
                  className={cn(
                    'flex h-7 items-center gap-1.5 rounded-full border px-2 text-[10px] font-bold transition-all',
                    heatmapFilter === lvl
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-neutral-200 bg-white text-neutral-400 hover:border-primary/40',
                  )}
                >
                  <div className={cn('h-3 w-3 rounded-sm', HEATMAP_COLORS[lvl])} />
                  {heatmapCounts[lvl]}
                </button>
              ))}
            </div>
            <div className="mb-1 flex gap-0.5 pl-12">
              {DAY_LABELS.map((d, i) => (
                <span
                  key={d}
                  className={cn(
                    'flex-1 text-center text-[9px] font-bold uppercase',
                    i === todayDayIndex ? 'text-primary' : 'text-neutral-300',
                  )}
                >
                  {d}
                </span>
              ))}
            </div>
            <div className="max-h-[70px] space-y-0.5 overflow-y-auto">
              {filteredHeatmap.map((row) => (
                <div key={row.etudiant.id} className="flex items-center gap-1">
                  <span
                    className="w-11 truncate text-right text-[9px] font-semibold text-neutral-500"
                    title={`${row.etudiant.prenom} ${row.etudiant.nom}`}
                  >
                    {row.etudiant.prenom}
                  </span>
                  <div className="flex flex-1 gap-0.5">
                    {row.dayPcts.map((pct, dayIdx) => {
                      const level = pct !== null ? heatmapLevel(pct) : 0
                      const isFuture = dayIdx > todayDayIndex
                      return (
                        <div
                          key={dayIdx}
                          className={cn(
                            'h-[10px] flex-1 rounded-sm',
                            isFuture ? 'bg-neutral-50' : HEATMAP_COLORS[level],
                          )}
                          title={pct !== null ? `${pct}%` : ''}
                        />
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-1 pl-12 text-[9px] text-neutral-400">
              <span>0%</span>
              {HEATMAP_COLORS.map((c, i) => (
                <div key={i} className={cn('h-2.5 w-2.5 rounded-sm', c)} />
              ))}
              <span>100%</span>
            </div>
          </div>

          {/* Scans — filters + fixed scroll */}
          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-neutral-900">
              <Leaf className="h-4 w-4 text-primary" />
              {t('dashboard.formateur.scans.title')}
            </h3>
            <div className="mb-2 flex flex-wrap gap-1.5">
              <FilterPill label={t('dashboard.formateur.scans.all')} count={scanCounts.all} active={scanFilter === 'all'} onClick={() => setScanFilter('all')} />
              <FilterPill label={t('dashboard.formateur.scans.done')} count={scanCounts.done} active={scanFilter === 'done'} onClick={() => setScanFilter('done')} />
              <FilterPill label={t('dashboard.formateur.scans.notDone')} count={scanCounts.notdone} active={scanFilter === 'notdone'} onClick={() => setScanFilter('notdone')} />
            </div>
            <div className="max-h-[160px] space-y-0.5 overflow-y-auto">
              {scansRows.length === 0 ? (
                <p className="py-3 text-center text-[11px] text-neutral-400">{t('dashboard.formateur.scans.noScans')}</p>
              ) : scansRows.map((row) => (
                <div key={row.etudiant.id} className="flex items-center gap-2 rounded-md px-2 py-[5px] odd:bg-neutral-50">
                  <Avatar etudiant={row.etudiant} size="sm" />
                  <span className="flex-1 truncate text-[11px] font-semibold text-neutral-700">
                    {row.etudiant.prenom} {row.etudiant.nom}
                  </span>
                  <span className={cn(
                    'rounded-md px-2 py-0.5 text-[9px] font-bold',
                    row.scanDone
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-red-50 text-red-500',
                  )}>
                    {row.scanDone
                      ? t('dashboard.formateur.scans.done')
                      : t('dashboard.formateur.scans.notDone')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Events — filters + fixed scroll */}
          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-neutral-900">
              <Calendar className="h-4 w-4 text-primary" />
              {t('dashboard.formateur.events.title')}
            </h3>
            <div className="mb-2 flex items-center gap-1.5">
              <FilterPill label={t('dashboard.formateur.events.all')} count={eventCounts.all} active={eventFilter === 'all'} onClick={() => setEventFilter('all')} />
              {([['CONTROLE', BookOpen], ['VACANCES', Palmtree], ['RELAX', PartyPopper]] as const).map(([type, Icon]) => (
                <button
                  key={type}
                  onClick={() => setEventFilter(eventFilter === type ? 'all' : type)}
                  className={cn(
                    'flex h-7 items-center gap-1 rounded-full border px-2 text-[10px] font-bold transition-all',
                    eventFilter === type
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-neutral-200 bg-white text-neutral-400 hover:border-primary/40',
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {eventCounts[type]}
                </button>
              ))}
            </div>
            <div className="max-h-[200px] space-y-0.5 overflow-y-auto">
              {filteredEvents.length === 0 ? (
                <p className="py-3 text-center text-[11px] text-neutral-400">{t(
                  eventFilter === 'CONTROLE' ? 'dashboard.formateur.events.noControle'
                    : eventFilter === 'VACANCES' ? 'dashboard.formateur.events.noVacances'
                    : eventFilter === 'RELAX' ? 'dashboard.formateur.events.noRelax'
                    : 'dashboard.formateur.events.noEvents'
                )}</p>
              ) : filteredEvents.map(({ event, studentNames, studentFullNames }) => {
                const typeClass = {
                  CONTROLE: 'bg-red-500',
                  VACANCES: 'bg-blue-500',
                  RELAX: 'bg-amber-500',
                }[event.type]
                const nameClass = {
                  CONTROLE: 'text-red-800',
                  VACANCES: 'text-blue-800',
                  RELAX: 'text-amber-800',
                }[event.type]
                const label = event.type === 'CONTROLE'
                  ? t('dashboard.formateur.events.testLabel', { subject: event.matiereNom ?? '' })
                  : event.type === 'VACANCES'
                    ? event.libelle ?? t('dashboard.formateur.events.holidayLabel')
                    : event.libelle ?? t('dashboard.formateur.events.relaxLabel')
                const dateStr = event.dateFin && event.dateFin !== event.dateDebut
                  ? `${formatEventDate(event.dateDebut, todayStr, tomorrowStr, t)} → ${formatEventDate(event.dateFin, todayStr, tomorrowStr, t)}`
                  : formatEventDate(event.dateDebut, todayStr, tomorrowStr, t)
                return (
                  <div key={event.id} className="flex items-center gap-2 rounded-md px-2 py-[7px] transition-colors hover:bg-neutral-50">
                    <div className={cn('h-1.5 w-1.5 shrink-0 rounded-full', typeClass)} />
                    <div className="min-w-0 flex-1">
                      <p className={cn('truncate text-[11px] font-semibold', nameClass)}>{label}</p>
                      <p className="text-[10px] text-neutral-400">{dateStr}</p>
                    </div>
                    <span
                      className="shrink-0 rounded-md bg-neutral-100 px-1.5 py-0.5 text-[9px] font-semibold text-neutral-500"
                      title={studentFullNames.join('\n')}
                    >
                      {studentNames.length === rows.length ? t('dashboard.formateur.events.allStudents') : studentNames.join(' · ')}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
